import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  parseTrialPolicyChanges,
  trialPolicyDelta,
  trialPolicyRows,
  type TrialPolicyChanges,
} from "@/lib/audit-log-changes";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { getAuditLogs, type AuditLogEntry } from "@/services/admin-api";
import { format } from "date-fns";

/**
 * The collapsed row's delta, drawn the way M13 draws it.
 *
 * **Four pieces, two of them hidden.** The operator READS `14 days → 21 days`. A screen reader
 * HEARS *"Changed from 14 days to 21 days"*, because the arrow is `aria-hidden` and the two words
 * are `sr-only` — so the relation is stated once for each, never twice for either.
 *
 * This was one flat string, which put the screen-reader words on screen and rendered the visibly
 * wrong *"Changed from 14 days → to 21 days"*. The stray *"→ to"* is the tell: it only parses if
 * one of the two was never meant to be seen.
 */
function TrialPolicyDeltaLine({ changes }: { changes: TrialPolicyChanges }) {
  const delta = trialPolicyDelta(changes);
  return (
    <span className="text-sm tabular-nums text-muted-foreground" data-testid="audit-trial-delta">
      <span className="sr-only">{delta.fromLabel}</span>
      {delta.from}
      <span aria-hidden="true"> → </span>
      <span className="sr-only">{delta.toLabel}</span>
      {delta.to}
    </span>
  );
}

function getActionVariant(action: string): "destructive" | "success" | "info" {
  if (action.includes("disable") || action.includes("cancel") || action.includes("delete")) {
    return "destructive";
  }
  if (action.includes("enable") || action.includes("add") || action.includes("extend")) {
    return "success";
  }
  return "info";
}

export default function AuditLogs() {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const pageSize = 50;

  const { data, isLoading } = useQuery({
    queryKey: ["audit-logs", page, pageSize],
    queryFn: () => getAuditLogs({ page, pageSize }),
  });

  const logs = data?.data || [];
  const pagination = data?.pagination;

  const handleToggleExpand = (logId: string) => {
    setExpandedId(expandedId === logId ? null : logId);
  };

  const handleExpandKeyDown = (e: React.KeyboardEvent, logId: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggleExpand(logId);
    }
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader title="Audit Logs" description="Track all admin actions for compliance" />

      <Card>
        <CardHeader>
          <CardTitle>All Audit Logs</CardTitle>
          <CardDescription>{pagination?.total.toLocaleString()} entries total</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {isLoading ? (
              <div className="py-8 text-center text-muted-foreground">Loading...</div>
            ) : logs.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">No audit logs found</div>
            ) : (
              logs.map((log: AuditLogEntry) => (
                <div key={log.id} className="rounded-lg border bg-card">
                  <div
                    className="flex cursor-pointer items-center justify-between p-4"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleToggleExpand(log.id)}
                    onKeyDown={(e) => handleExpandKeyDown(e, log.id)}
                  >
                    <div className="flex items-center gap-4">
                      <Badge variant={getActionVariant(log.action)}>{log.action}</Badge>
                      <span className="text-sm">
                        <span className="font-medium">{log.admin.name || log.admin.email}</span>
                        {" on "}
                        <span className="text-muted-foreground">
                          {log.targetType}:{log.targetId.slice(0, 8)}...
                        </span>
                      </span>
                      {/* One extra line on the collapsed row, for trial-policy changes only, so
                          the two values are legible without expanding. `tabular-nums` and no
                          colour: it is information, not a status.

                          It carries the UNIT WORD. `Trial duration 14 → 21` does not say what
                          unit it is in, on a page listing eleven other action types — and this
                          run carries an amendment whose whole subject is that a duration
                          without its inflected unit reads wrong. */}
                      {parseTrialPolicyChanges(log.changes) !== null && (
                        <TrialPolicyDeltaLine changes={parseTrialPolicyChanges(log.changes)!} />
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(log.createdAt), "MMM d, yyyy HH:mm")}
                      </span>
                      {expandedId === log.id ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>

                  {expandedId === log.id && (
                    <div className="border-t bg-muted/50 p-4">
                      <div className="grid gap-4 text-sm md:grid-cols-2">
                        <div>
                          <div className="font-medium text-muted-foreground">Admin</div>
                          <div>{log.admin.email}</div>
                        </div>
                        <div>
                          <div className="font-medium text-muted-foreground">Target</div>
                          <div>
                            {log.targetType}: {log.targetId}
                          </div>
                        </div>
                        <div>
                          <div className="font-medium text-muted-foreground">IP Address</div>
                          <div>{log.ipAddress || "Unknown"}</div>
                        </div>
                        <div>
                          <div className="font-medium text-muted-foreground">User Agent</div>
                          <div className="truncate" title={log.userAgent || "Unknown"}>
                            {log.userAgent || "Unknown"}
                          </div>
                        </div>
                        {/* A trial-policy change renders as three named rows rather than as raw
                            JSON. The requirement is that the log shows who changed the trial
                            length, from what, to what and why —
                            `{"setting":"trialDurationDays","previousValue":14,…}` satisfies the
                            letter of that and not the point.

                            The reason row spans BOTH columns. Without that, three single cells
                            shift everything after them by one and orphan User Agent — for this
                            one action type only, and nothing in this page's tests pins the
                            layout, so it would have gone unnoticed.

                            Every other action type keeps the `<pre>`, which is right for eleven
                            shapes nobody has designed a view for. */}
                        {parseTrialPolicyChanges(log.changes) !== null
                          ? trialPolicyRows(parseTrialPolicyChanges(log.changes)!).map((row) => (
                              <div
                                key={row.label}
                                className={row.spanTwo ? "md:col-span-2" : undefined}
                              >
                                <div className="font-medium text-muted-foreground">{row.label}</div>
                                <div>{row.value}</div>
                              </div>
                            ))
                          : log.changes && (
                              <div className="md:col-span-2">
                                <div className="font-medium text-muted-foreground">Changes</div>
                                <pre className="mt-1 rounded bg-muted p-2 text-xs overflow-auto">
                                  {JSON.stringify(log.changes, null, 2)}
                                </pre>
                              </div>
                            )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, pagination.total)}{" "}
                of {pagination.total}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
